/**
 * SellerMiniCard — one inline quote `seller_match` card inside an assistant
 * quote-review bubble (design revamp).
 *
 * Production translation of the prototype `SellerMiniCard`
 * (`docs/GardEn/handoff-2026-06/garden/screens-chat.jsx`): an Avatar (derived
 * initials on a deterministic coloured disc) + the seller name + a
 * star·rating·district line + the price (`сом/{unit}`), where the cheapest match
 * renders the price in the success colour.
 *
 * Data sources:
 *   • name / price / unit / cheapest — from the streamed `seller_match` card.
 *   • rating + district             — looked up from the seller directory
 *                                     (`useSellerDirectory`); absent → the
 *                                     rating row is omitted (never fabricated).
 *   • avatar initials + colour      — derived from id/name (`seller-display`).
 *
 * Navigation: `/shops/sellers/:sellerId` (SCRUM-140) opens the seller finder
 * with that seller's detail `BottomSheet` pre-opened — `ShopsScreen` resolves
 * the id against the loaded list, falling back to a directory fetch when the
 * seller isn't in the current filter. It is keyboard-accessible; "cheapest" is
 * conveyed by a text label, NEVER colour alone (WCAG — colour is not the sole
 * signal). Tokens come from the `--g-*` mirror; the seller name is DB text
 * rendered AS text (never HTML); pricing is `tabular-nums`.
 */
import { Avatar, Icon } from "@garden/ui";
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { fmtNum, parseDecimal } from "@/lib/format";
import type { SellerMatchCardData } from "@/features/chat/useChatStream";
import { useSellerDirectory } from "@/features/chat/useSellerDirectory";
import {
  sellerColor,
  sellerInitials,
} from "@/features/chat/seller-display";

/** The seller finder a chat seller-match card links to (A0 — no dead-end). */
export const SHOPS_ROUTE = "/shops";

/** Deep-linkable seller detail route (SCRUM-140). */
export function sellerDetailRoute(sellerId: string): string {
  return `/shops/sellers/${sellerId}`;
}

export interface SellerMiniCardProps {
  card: SellerMatchCardData;
}

export function SellerMiniCard({ card }: SellerMiniCardProps): ReactElement {
  const { t } = useTranslation("chat");
  const { byId } = useSellerDirectory();
  const seller = byId.get(card.seller_id);

  const priceNum = parseDecimal(card.price_kgs);
  const priceText = fmtNum(priceNum);
  const unitText = t("seller.unit", { unit: card.unit });

  // Rating row only when the directory has resolved the seller (no fabrication).
  const ratingNum = seller?.rating != null ? parseDecimal(seller.rating) : null;
  const district = seller?.district ?? null;
  const ratingText =
    ratingNum !== null && district !== null
      ? `${ratingNum.toFixed(1)} · ${district}`
      : ratingNum !== null
        ? ratingNum.toFixed(1)
        : (district ?? null);

  return (
    <Link
      to={sellerDetailRoute(card.seller_id)}
      data-testid={`chat-seller-card-${card.seller_id}`}
      data-cheapest={card.cheapest ? "true" : "false"}
      aria-label={t("seller.openInShops", { name: card.business_name })}
      className="flex min-h-[44px] items-center gap-3 rounded-[var(--g-radius)] border border-border bg-background p-3 text-foreground transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <Avatar
        initials={sellerInitials(card.business_name)}
        color={sellerColor(card.seller_id)}
        size={38}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14.5px] font-bold text-foreground">
          {card.business_name}
        </div>
        {ratingText !== null ? (
          <div className="mt-0.5 flex items-center gap-1">
            <Icon
              name="star"
              size={11}
              className="text-[color:var(--g-warn)]"
            />
            <span className="truncate text-[12.5px] tabular-nums text-muted-foreground">
              {ratingText}
            </span>
          </div>
        ) : null}
      </div>
      <div className="shrink-0 text-right">
        <div
          className={
            "text-[15px]/[1.429] font-bold tabular-nums " +
            (card.cheapest
              ? "text-[color:var(--g-success)]"
              : "text-foreground")
          }
        >
          {priceText}
        </div>
        <div className="text-[11.5px] text-muted-foreground">{unitText}</div>
        {card.cheapest ? (
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--g-success)]">
            {t("seller.cheapest")}
          </div>
        ) : null}
      </div>
    </Link>
  );
}

export default SellerMiniCard;
